<template>
    <div v-if="badges.length" class="trip-event-badges d-flex flex-wrap align-center ga-1">
        <v-tooltip v-for="b in badges" :key="b.key" location="top" :text="b.tooltip">
            <template #activator="{ props: tp }">
                <span class="tev-pill" :style="pillStyle(b.color)" v-bind="tp">
                    <v-icon :icon="b.icon" size="12" :color="b.color"/>
                    <span v-if="b.value !== ''" class="tev-val" :style="{ color: b.color }">{{ b.value }}</span>
                </span>
            </template>
        </v-tooltip>
    </div>
</template>

<script setup>
// Compact "what happened on this trip" badges (hard braking / accel / turns /
// speeding / crash) for the Activity trip card. Renders nothing for a clean trip.
// Reads the same driver-analytics fields as TripScoreDialog (present only for
// trips with firmware accelerometer data), so GPS-only trips simply show no
// badges. Colors match the trip-route map + score dialog for one visual language.
import {computed} from "vue";

const props = defineProps({trip: {type: Object, required: true}});

const COLOR = {
    crash: '#FF1744',
    brake: '#E53935',
    accel: '#8E24AA',
    turn: '#FF6F00',
    speed: '#FB8C00',
};

const da = computed(() => props.trip?.driver_analytics);
const se = computed(() => props.trip?.score_events);
const field = (name) => da.value?.[name] ?? props.trip?.[name] ?? se.value?.[name];
const num = (v) => {
    const n = Number(v);
    return isNaN(n) ? 0 : Math.max(0, n);
};

const braking = computed(() => num(field('harsh_braking_count')));
const accel = computed(() => num(field('harsh_accel_count')));
const cornering = computed(() => num(field('sharp_cornering_count')));
const crash = computed(() => num(field('crash_count')));
const speedingSec = computed(() => num(field('speeding_seconds')));

const fmtDur = (secs) => {
    const s = parseInt(secs);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    return `${m}m${rs > 0 ? ` ${rs}s` : ''}`;
};

const badges = computed(() => {
    const out = [];
    // Crash is most severe - lead with it. Count only shown when >1 (a lone icon
    // reads clearly as "a crash").
    if (crash.value > 0) {
        out.push({key: 'crash', icon: 'mdi-car-emergency', color: COLOR.crash,
            value: crash.value > 1 ? crash.value : '', tooltip: 'Crash detected'});
    }
    if (braking.value > 0) {
        out.push({key: 'brake', icon: 'mdi-car-brake-alert', color: COLOR.brake,
            value: braking.value, tooltip: `${braking.value} hard brake${braking.value > 1 ? 's' : ''}`});
    }
    if (accel.value > 0) {
        out.push({key: 'accel', icon: 'mdi-rocket-launch', color: COLOR.accel,
            value: accel.value, tooltip: `${accel.value} hard acceleration${accel.value > 1 ? 's' : ''}`});
    }
    if (cornering.value > 0) {
        out.push({key: 'turn', icon: 'mdi-steering', color: COLOR.turn,
            value: cornering.value, tooltip: `${cornering.value} hard turn${cornering.value > 1 ? 's' : ''}`});
    }
    if (speedingSec.value > 0) {
        out.push({key: 'speed', icon: 'mdi-speedometer', color: COLOR.speed,
            value: fmtDur(speedingSec.value), tooltip: `Speeding for ${fmtDur(speedingSec.value)}`});
    }
    return out;
});

const hexToRgba = (hex, a) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};
const pillStyle = (color) => ({
    backgroundColor: hexToRgba(color, 0.12),
    border: `1px solid ${hexToRgba(color, 0.22)}`,
});
</script>

<style scoped>
.tev-pill {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 1px 7px 1px 5px;
    border-radius: 999px;
    line-height: 1.5;
    white-space: nowrap;
}
.tev-val {
    font-size: 11px;
    font-weight: 700;
}
</style>
